import { useState, useEffect, memo } from "react";
import Avatar from "@mui/material/Avatar";
import { useProductImage } from "../state/productImageState";

/**
 * ProductImageAvatar
 * Reusable avatar that fetches an authenticated product image blob via useProductImage
 * Props:
 *  - productCode | code: string (either prop name accepted)
 *  - size: number (px)
 *  - sx: MUI sx override
 *  - fallback: optional fallback content (defaults to first letter of code)
 */
export const ProductImageAvatar = memo(function ProductImageAvatar({
  productCode,
  code,
  size = 64,
  sx = {},
  fallback,
  preferredFormats = ["png"],
}) {
  const finalCode = productCode || code;
  const { data: blob, isLoading } = useProductImage(finalCode, !!finalCode, {
    preferredFormats,
    extension: "png", // force PNG only for avatar (temporary)
  });
  const [objectUrl, setObjectUrl] = useState();

  useEffect(() => {
    if (blob instanceof Blob) {
      const url = URL.createObjectURL(blob);
      setObjectUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setObjectUrl(undefined);
    }
  }, [blob]);

  return (
    <Avatar
      variant="rounded"
      src={objectUrl}
      alt={objectUrl ? "" : finalCode}
      sx={{ width: size, height: size, opacity: isLoading ? 0.6 : 1, ...sx }}
      slotProps={{
        img: {
          width: size,
          height: size,
          decoding: "async",
          loading: "lazy",
        },
      }}
    >
      {objectUrl ? null : fallback || finalCode?.charAt(0)}
    </Avatar>
  );
});

export default ProductImageAvatar;
